import { combineReducers, configureStore, type Reducer } from "@reduxjs/toolkit";
import { persistStore } from "redux-persist";

import { api } from "../shared/api/api";
import { constantsApi } from "../shared/api/constantsApi";
import analyticReducer from "./analytics/analyticsReducers";
import { appListenerMiddleware } from "./app/appListeners";
import appReducer from "./app/appReducers";
import pendingTransactionsReducer from "./pendingTransactions/pendingTransactionsReducer";
import { toastsMiddleware } from "./toasts/toastsMiddleware";

interface asyncReducerType {
  [sliceName: string]: Reducer;
}

const middlewares = [appListenerMiddleware.middleware, toastsMiddleware, api.middleware, constantsApi.middleware];

function createReducer(asyncReducers: asyncReducerType) {
  return combineReducers({
    [api.reducerPath]: api.reducer,
    [constantsApi.reducerPath]: constantsApi.reducer,
    app: appReducer,
    analytic: analyticReducer,
    pendingTransactions: pendingTransactionsReducer,
    ...asyncReducers,
  });
}

const store = configureStore({
  reducer: createReducer({}),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST"], // Ignore the persist/PERSIST action
      },
    }).concat(middlewares),
  devTools: !import.meta.env.PROD,
});

function reducerManagerCreator(storeInstance: typeof store) {
  const asyncReducerMap: asyncReducerType = {};

  const injectReducer = (sliceName: string, reducer: Reducer) => {
    if (!asyncReducerMap?.sliceName) {
      asyncReducerMap[sliceName] = reducer;
    }
    storeInstance.replaceReducer(createReducer(asyncReducerMap));
  };

  return injectReducer;
}

export const reducerManager = reducerManagerCreator(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);

export default store;
